import React from 'react';
import { Flag, Info, ListChecks, RefreshCw } from 'lucide-react';
import { RegulationLink } from '../ui/RegulationLink';
type ClaimVerdict = 'Permitted' | 'Permitted with conditions' | 'Verification required' | 'Not permitted';
type Claim = {
  text: string;
  classification: 'Indication' | 'Presentation' | 'Voluntary';
  verdict: ClaimVerdict;
  reference?: string;
  referenceUrl?: string;
  extract?: string;
  needsEvidence?: boolean;
  recommendation?: string;
  basis?: string;
  needsCertification?: boolean;
};
const indicationsReference = 'Therapeutic Goods (Permissible Indications) Determination';
const indicationsExtract = 'An indication for a listed medicine must be selected from the indications set out in the Permissible Indications Determination and must be supported by evidence held by the sponsor.';
const advertisingReference = 'Therapeutic Goods Advertising Code';
const advertisingExtract = 'An advertisement for therapeutic goods must not be misleading, must not disparage other goods, and must not contain statements that are inconsistent with the regulatory framework applying in Australia.';
const claims: Claim[] = [{
  text: 'Supports immune function',
  classification: 'Indication',
  verdict: 'Permitted with conditions',
  reference: indicationsReference,
  referenceUrl: 'https://www.legislation.gov.au/F2025L00450/asmade/text',
  extract: indicationsExtract,
  needsEvidence: true,
  recommendation: 'Indications must be selected verbatim from the Permissible Indications Determination. Map this to the corresponding listed indication.'
}, {
  text: 'Promotes collagen and connective tissue formation',
  classification: 'Indication',
  verdict: 'Permitted with conditions',
  reference: indicationsReference,
  extract: indicationsExtract,
  recommendation: 'Map to the corresponding listed indication; free-text wording is not permitted.'
}, {
  text: 'Supports immune function, vision, red blood cell and bone formation',
  classification: 'Indication',
  verdict: 'Permitted with conditions',
  reference: indicationsReference,
  extract: indicationsExtract,
  recommendation: 'Split into separate indications, each mapped verbatim to its own PID entry for the relevant active ingredient.'
}, {
  text: 'Supports immune and nervous system function; promotes growth and development',
  classification: 'Indication',
  verdict: 'Verification required',
  reference: indicationsReference,
  extract: indicationsExtract,
  recommendation: "Remove 'promotes growth and development' — a paediatric-coded claim, not applicable. Keep the immune and nervous system claim mapped to exact PID wording for the adult population."
}, {
  text: 'This product is not intended to diagnose, treat, cure, or prevent any disease.',
  classification: 'Presentation',
  verdict: 'Not permitted',
  reference: advertisingReference,
  extract: advertisingExtract,
  recommendation: 'United States DSHEA disclaimer with no standing in Australia. Remove.'
}, {
  text: 'Instead of the usual synthetic fillers, we use fruit powders made with real fruit juices',
  classification: 'Presentation',
  verdict: 'Not permitted',
  reference: advertisingReference,
  extract: advertisingExtract,
  recommendation: 'Disparaging comparison against other products. Remove or reword.'
}, {
  text: 'SATISFACTION GUARANTEED',
  classification: 'Presentation',
  verdict: 'Not permitted',
  reference: advertisingReference,
  extract: advertisingExtract,
  recommendation: 'Guarantee-style claims are restricted. Remove.'
}, {
  text: '619% DV VITAMIN C · 113% DV VITAMIN A · 133% DV ZINC',
  classification: 'Presentation',
  verdict: 'Not permitted',
  reference: 'TGO 92 — Standard for labels of non-prescription medicines',
  extract: 'Quantitative information on a medicine label must be expressed using the units and reference values that apply in Australia.',
  recommendation: 'Percent Daily Value is a United States construct. Remove or restate against Australian reference values.'
}, {
  text: 'Add to any of your favourite smoothies, yogurt or oatmeal',
  classification: 'Presentation',
  verdict: 'Not permitted',
  reference: 'Therapeutic Goods Act 1989 s3(5) / Therapeutic Goods Regulations 1990 s3A — unacceptable presentation as food',
  extract: 'Directions for use must be appropriate to the therapeutic use of the product and not present it as a conventional food.',
  recommendation: 'Remove; replace with standard therapeutic directions for use (dose, form, timing).'
}, {
  text: 'VEGAN',
  classification: 'Voluntary',
  verdict: 'Permitted',
  basis: 'Checked against the formulation: every ingredient is plant-derived, mineral or synthetic. No animal-derived material is present.'
}, {
  text: 'GLUTEN-FREE',
  classification: 'Voluntary',
  verdict: 'Verification required',
  basis: 'Checked against the formulation: no gluten-containing ingredient is present, but the corn-derived fibre and flavour blend are supplier inputs with possible shared-line exposure.',
  needsCertification: true
}, {
  text: 'NON-GMO',
  classification: 'Voluntary',
  verdict: 'Verification required',
  basis: 'Checked against the formulation: the corn-derived resistant maltodextrin and the proprietary flavour blend are the exposed inputs. Neither carries a non-GMO status in the dossier.',
  needsCertification: true
}, {
  text: 'NO ARTIFICIAL SWEETENERS',
  classification: 'Voluntary',
  verdict: 'Permitted',
  basis: 'Checked against the formulation: the only sweetener is stevia extract, a plant-derived sweetener.'
}, {
  text: 'NO ARTIFICIAL FLAVORS',
  classification: 'Voluntary',
  verdict: 'Permitted',
  basis: 'Checked against the formulation: flavour is contributed by the fruit juice concentrates and a natural orange flavour blend.'
}, {
  text: 'NO PRESERVATIVES',
  classification: 'Voluntary',
  verdict: 'Permitted',
  basis: 'Checked against the formulation: no preservative is declared. Silicon dioxide is an anticaking agent, not a preservative.'
}];
interface ClaimsTabProps {
  onBackClick: () => void;
}
export function ClaimsTab(_props: ClaimsTabProps) {
  return <ClaimsResults data-id="element-70" data-mp="1cai9a0shvj1u" />;
}
function ClaimsResults() {
  return <div className="space-y-4" data-id="element-71" data-mp="ojwdp8zofr8q">
      <header className="flex items-start gap-3" data-id="element-72" data-mp="ol7d6wm3acu1">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#eff3ed] text-forest-green" data-id="element-73" data-mp="jll2attqh8oe">
          <ListChecks className="h-5 w-5" data-id="element-74" data-mp="xczkiyfth5ue" />
        </span>
        <div data-id="element-75" data-mp="xeki6wxfh4wh">
          <h2 className="font-[Figtree] text-2xl font-semibold text-evergreen" data-id="element-76" data-mp="sm113pzj7rmx">Claims</h2>
          <p className="mt-1 text-sm leading-relaxed text-[#6b7d70]" data-id="element-77" data-mp="4wf5tdma5wk7">Claims extracted from the label and assessed against the TGA framework.</p>
        </div>
      </header>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-78" data-mp="ul7vd3gz6nq">
        <p className="text-sm text-[#718174]" data-id="element-79" data-mp="3mupxzi6j15a2">{claims.length} claims extracted from 3 label faces · analysed 26 Jul 2026</p>
        <button type="button" className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-lg border border-[#d6dfd3] bg-white px-3 py-2 text-sm font-semibold text-evergreen transition hover:bg-[#f7faf5] sm:self-auto" data-id="element-80" data-mp="3rou45z7yx9a3">
          <RefreshCw className="h-4 w-4" data-id="element-81" data-mp="9guxmg6y3qgh" />
          Re-analyse
        </button>
      </div>

      <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-relaxed text-amber-900" data-id="element-82" data-mp="eg7hcs2isheu">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" data-id="element-83" data-mp="3vo2x51v6p34l" />
        Five claims are not usable in Australia. The label is written to the United States framework — indications must be selected verbatim from the Permissible Indications Determination, and several presentation elements have no standing under the Therapeutic Goods Advertising Code.
      </div>

      {claims.map(claim => <ClaimCard key={claim.text} claim={claim} data-id="element-84" data-mp="26isr0z28uqn0" />)}
      <div className="flex items-start gap-2 rounded-xl border border-[#dbe4d8] bg-[#f5f8f3] px-4 py-3 text-sm leading-relaxed text-[#607266]" data-id="element-85" data-mp="21iwluwht8t5v"><Info className="mt-0.5 h-4 w-4 shrink-0 text-[#718174]" data-id="element-86" data-mp="1wkv3yew4ezvz" />Suggestions only — not a verification. Each claim must be confirmed and, where indicated, substantiated before use.</div>
    </div>;
}
function ClaimCard({
  claim
}: {
  claim: Claim;
}) {
  return <article className="rounded-2xl border border-[#dce3da] bg-white p-5 shadow-sm" data-id="element-87" data-mp="2k9eaopziv4ly">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between" data-id="element-88" data-mp="2kbqr2ypmt6ww">
        <h3 className="font-[Figtree] text-lg font-semibold text-evergreen" data-id="element-89" data-mp="2fgl7w7nm66ci">{claim.text}</h3>
        <div className="flex shrink-0 items-center gap-2" data-id="element-90" data-mp="2bgub9nbeow02"><ClassificationTag label={claim.classification} data-id="element-91" data-mp="37zihf680bkh6" /><VerdictBadge verdict={claim.verdict} data-id="element-92" data-mp="380a0xiobxrr3" /></div>
      </div>
      <div className="mt-5" data-id="element-93" data-mp="3377fjxtpholz">
        {claim.reference && <>
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-[#7a897d]" data-id="element-94" data-mp="ppm9ytf9q0t1">Regulatory reference</p>
          <p className="mt-1" data-id="element-95" data-mp="unxjf31ni9aq"><RegulationLink text={claim.reference} href={claim.referenceUrl} data-id="element-96" data-mp="upicspc8bm70" /></p>
        </>}
        {claim.extract && <blockquote className="mt-3 border-l-2 border-lime-glow pl-3 text-sm italic leading-relaxed text-[#637467]" data-id="element-97" data-mp="yor7pxj09q45">“{claim.extract}”</blockquote>}
        {claim.basis && <p className="text-sm leading-relaxed text-[#506355]" data-id="element-98" data-mp="18p9x17tc9ytg">{claim.basis}</p>}
        {claim.needsEvidence && <p className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#617366]" data-id="element-99" data-mp="1dmrvu5dn6xnq"><Flag className="h-4 w-4 text-[#728477]" data-id="element-100" data-mp="1idrubf2etnsc" />Needs: substantiating evidence</p>}
        {claim.needsCertification && <p className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#617366]" data-id="element-101" data-mp="1metuby45i3vd"><Flag className="h-4 w-4 text-[#728477]" data-id="element-102" data-mp="1rfhsr4uplmzm" />Needs: supplier certification</p>}
        {claim.recommendation && <div className="mt-4" data-id="element-103" data-mp="1wd9jvwnj552r"><p className="font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-[#7a897d]" data-id="element-104" data-mp="1wekpm20jcmwi">Recommendation</p><p className="mt-1 text-sm leading-relaxed text-[#506355]" data-id="element-105" data-mp="1nsgg2r3k6pgq">{claim.recommendation}</p></div>}

      </div>
    </article>;
}
function ClassificationTag({
  label
}: {
  label: Claim['classification'];
}) {
  return <span className="rounded-md bg-[#f0f3ef] px-2 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.13em] text-[#637467]" data-id="element-106" data-mp="1wp790umtdo73">{label}</span>;
}
function VerdictBadge({
  verdict
}: {
  verdict: ClaimVerdict;
}) {
  const style: Record<ClaimVerdict, string> = {
    Permitted: 'bg-forest-green/10 text-forest-green ring-forest-green/20',
    'Permitted with conditions': 'bg-amber-100 text-amber-800 ring-amber-200',
    'Verification required': 'bg-slate-100 text-slate-700 ring-slate-200',
    'Not permitted': 'bg-red-100 text-red-800 ring-red-200'
  };
  return <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${style[verdict]}`} data-id="element-107" data-mp="m2l2qv6vzvhj">{verdict}</span>;
}